import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { FC, memo, useEffect, useState } from 'react';
import { appBarHeight } from 'src/constants/layout';
import { useCoursesQuery } from 'src/hooks/useCourses.query';
import { useTimetablesQuery } from 'src/hooks/useTimetables.query';

import CourseDetail from './CourseDetail';
import CourseList from './CourseList';
import NewCourseDialog from './NewCourseDialog';
import NewTimetableDialog from './NewTimetableDialog';
import TimetableDetail from './TimetableDetail';
import TimetableList from './TimetableList';

export type Selection =
  | { kind: 'timetable'; timetableId: number }
  /** returnTimetableId はダイヤ詳細から開いたときの元のダイヤ（「← ○○ダイヤへ戻る」の行き先） */
  | { kind: 'course'; courseId: number; timetableId: number | null; returnTimetableId: number | null };
type ListMode = 'timetables' | 'courses';

const border = 1;

/** ダイヤ・コースマスタ（log_web_issues#347）。左: ダイヤ／コースの一覧、右: 選んだダイヤかコースの詳細 */
const CoursesPresenter: FC = memo(() => {
  const [listMode, setListMode] = useState<ListMode>('timetables');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [newTimetableOpen, setNewTimetableOpen] = useState(false);
  const [newCourseOpen, setNewCourseOpen] = useState(false);
  const timetables = useTimetablesQuery();
  const courses = useCoursesQuery();

  const openTimetable = (timetableId: number) => {
    setListMode('timetables');
    setSelection({ kind: 'timetable', timetableId });
  };
  const returnTimetable = selection?.kind === 'course' && selection.returnTimetableId != null
    ? timetables.data?.find((t) => t.id === selection.returnTimetableId)
    : undefined;

  // 試作と同じく最初のダイヤを選んだ状態で開く
  useEffect(() => {
    if (selection == null && timetables.data?.[0]) setSelection({ kind: 'timetable', timetableId: timetables.data[0].id });
  }, [selection, timetables.data]);

  return (
    <>
      <title>ダイヤ・コースマスタ</title>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${appBarHeight}px)`, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, height: 56, borderBottom: border, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ fontSize: 18, fontWeight: 700 }}>ダイヤ・コースマスタ</Box>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<AddRoundedIcon />}
            onClick={() => (listMode === 'timetables' ? setNewTimetableOpen(true) : setNewCourseOpen(true))}
          >
            {listMode === 'timetables' ? 'ダイヤを追加' : 'コースを追加'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box sx={{ width: '38%', minWidth: 420, maxWidth: 600, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: border, borderColor: 'divider' }}>
            <Tabs value={listMode} onChange={(_e, value: ListMode) => setListMode(value)} aria-label="一覧の表示" sx={{ borderBottom: border, borderColor: 'divider', minHeight: 44 }}>
              <Tab value="timetables" label="ダイヤ" />
              <Tab value="courses" label="コース" />
            </Tabs>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {listMode === 'timetables' && (
                <TimetableList
                  timetables={timetables.data ?? []}
                  selectedTimetableId={selection?.kind === 'timetable' ? selection.timetableId : null}
                  onSelectTimetable={(timetableId) => setSelection({ kind: 'timetable', timetableId })}
                />
              )}
              {listMode === 'courses' && (
                <CourseList courses={courses.data ?? []} selectedCourseId={selection?.kind === 'course' ? selection.courseId : null}
                  onSelect={(courseId) => {
                    const course = courses.data?.find((c) => c.id === courseId);
                    setSelection({ kind: 'course', courseId, timetableId: course?.timetables[0]?.id ?? null, returnTimetableId: null });
                  }} />
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            {selection == null && <Box sx={{ p: 4, color: 'text.secondary' }}>ダイヤを選択してください</Box>}
            {selection?.kind === 'timetable' && (
              <TimetableDetail timetableId={selection.timetableId} courses={courses.data ?? []}
                coursesLoaded={courses.data != null}
                onCourse={(courseId) => setSelection({ kind: 'course', courseId, timetableId: selection.timetableId, returnTimetableId: selection.timetableId })}
                onDeleted={() => setSelection(null)} />
            )}
            {returnTimetable && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => openTimetable(returnTimetable.id)} sx={{ color: 'text.secondary', justifyContent: 'flex-start', textAlign: 'left' }}>
                  {returnTimetable.name}ダイヤへ戻る
                </Button>
              </Box>
            )}
            {selection?.kind === 'course' && (
              <CourseDetail
                courseId={selection.courseId}
                courses={courses.data ?? []}
                selectedTimetableId={selection.timetableId}
                onSelectTimetable={(timetableId) => setSelection({ ...selection, timetableId })}
                onOpenTimetable={openTimetable}
                onDeleted={() => setSelection(null)}
              />
            )}
          </Box>
        </Box>
        <NewTimetableDialog
          open={newTimetableOpen}
          onClose={() => setNewTimetableOpen(false)}
          onCreated={(t) => {
            setNewTimetableOpen(false);
            setListMode('timetables');
            setSelection({ kind: 'timetable', timetableId: t.id });
          }}
        />
        {newCourseOpen && (
          <NewCourseDialog
            open={newCourseOpen}
            courses={courses.data ?? []}
            onClose={() => setNewCourseOpen(false)}
            onCreated={(c) => {
              setNewCourseOpen(false);
              setListMode('courses');
              setSelection({ kind: 'course', courseId: c.id, timetableId: null, returnTimetableId: null });
            }}
          />
        )}
      </Box>
    </>
  );
});

export default CoursesPresenter;
